import EXIF from '../../3rd_party/exifjs/exif.js';

export default class pegPhotoSelector extends HTMLElement {
	async connectedCallback() {
		await new Promise(requestAnimationFrame); //wait a tick
		this.abortController = new AbortController();
		this.#setupEventListeners();
		this.#initDragAndDropFileSelection();
	}

	disconnectedCallback() {
		this.abortController.abort();
	}

	#setupEventListeners() {
		this.addEventListener(
			'pegPhotoUploader:uploadFailed',
			() => {
				document.querySelector('#errorDialog500').openModal();
				this.#trackValidationError('unknown');
			},
			{ signal: this.abortController.signal },
		);

		this.addEventListener(
			'pegPhotoUploader:maxPhotosReached',
			() => {
				this.querySelector('#errorMaxImageReached').openModal();
				this.#trackValidationError('max_reached');
			},
			{ signal: this.abortController.signal },
		);

		this.querySelector('#uploadPicture').addEventListener(
			'click',
			() => {
				this.openPhotoPicker();
			},
			{ signal: this.abortController.signal },
		);

		this.querySelector('#uploadPictureField').addEventListener(
			'change',
			(event) => {
				this.#validateAndPresentImage(event.target.files[0]);
			},
			{ signal: this.abortController.signal },
		);
	}

	#initDragAndDropFileSelection() {
		const dragAndDropArea = this.querySelector('#dragDropArea');

		//this is needed to prevent the browser from opening the image inside another tab
		for (const eventNamesToStopBrowserDefaults of ['dragenter', 'dragover', 'dragleave', 'drop']) {
			document.addEventListener(
				eventNamesToStopBrowserDefaults,
				(event) => {
					event.preventDefault();
					event.stopPropagation();
				},
				{ signal: this.abortController.signal },
			);
		}

		dragAndDropArea.addEventListener(
			'dragenter',
			() => {
				dragAndDropArea.classList.add('dragOver');
			},
			{ signal: this.abortController.signal },
		);

		dragAndDropArea.addEventListener(
			'dragover',
			() => {
				dragAndDropArea.classList.add('dragOver');
			},
			{ signal: this.abortController.signal },
		);

		dragAndDropArea.addEventListener(
			'dragleave',
			() => {
				dragAndDropArea.classList.remove('dragOver');
			},
			{ signal: this.abortController.signal },
		);

		dragAndDropArea.addEventListener(
			'drop',
			(event) => {
				this.#validateAndPresentImage(event.dataTransfer.files[0]);
				this.#trackInitUploadButton();
			},
			{ signal: this.abortController.signal },
		);
	}

	openPhotoPicker() {
		this.#hideAllUploadErrors();
		this.querySelector('#uploadPictureField').click();
		this.#trackInitUploadButton();
	}
	//todo: should be inside of the peg-photo-upload-dialog
	// it likely cant, as the template of the dialog will get copied
	async #startEditing(photo) {
		await this.querySelector('#photoEditorDialog').openModal();
		this.querySelector('peg-photo-editor').startCropper(photo);
	}

	async #validateAndPresentImage(imageFile) {
		try {
			this.#hideAllUploadErrors();

			if (!imageFile) {
				throw { type: 'no_file' };
			}
			if (!navigator.onLine) {
				throw { type: 'offline' };
			}

			this.#isValidFileType(imageFile);
			this.#isValidFileSize(imageFile);
			await this.#isValidImageDimensions(imageFile);
			const imageExif = await this.#readExif(imageFile);
			this.creationDate = this.#transformToIsoDate(imageExif?.DateTimeOriginal || imageExif?.DateTime);
			this.#trackPhotoSelected();
			await this.#presentImage(imageFile);
		} catch (error) {
			console.error(error);
			this.#handleValidationError(error);
		}
	}

	#handleValidationError(error) {
		switch (error?.type) {
			case 'wrong_format':
				this.#showWrongFileTypeError();
				break;
			case 'file_too_big':
				this.#showFileSizeExceededError();
				break;
			case 'file_dimensions':
				this.#showImageToSmallError();
				break;
			case 'no_file':
			case 'file_unavailable':
				this.#showImageCorruptedError();
				break;
			case 'offline':
				this.#showOfflineError();
				break;
			default:
				this.#showUnknownError();
				break;
		}
	}

	#transformToIsoDate(creationDate) {
		// if the date is not in the data, we just assume it was created just now
		if (!creationDate) {
			return new Date().toJSON();
		}
		const splitDate = creationDate.split(/\D/);
		return new Date(splitDate[0], splitDate[1] - 1, splitDate[2], splitDate[3], splitDate[4], splitDate[5]).toJSON();
	}

	async #readExif(imageFile) {
		const buffer = await imageFile.arrayBuffer();
		return EXIF.readFromBinaryFile(buffer);
	}

	#presentImage(imageFile) {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = async (event) => {
				await this.#startEditing(event.target.result);
				this.querySelector('#uploadPictureField').value = null;
				resolve();
			};
			reader.readAsDataURL(imageFile);
		});
	}

	#showWrongFileTypeError() {
		this.querySelector('#errorBox_wrongFiletype').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('wrong_format');
	}

	#showFileSizeExceededError() {
		this.querySelector('#errorBox_filesizeExceeded').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('file_too_big');
	}

	#showImageToSmallError() {
		this.querySelector('#errorBox_imageToSmall').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('file_dimensions');
	}

	#showImageCorruptedError() {
		this.querySelector('#errorBox_imageCorrupted').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('file_unavailable');
	}

	#showOfflineError() {
		this.querySelector('#errorBox_offline').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('offline');
	}

	#showUnknownError() {
		this.querySelector('#errorBox_unknown').setAttribute('show', 'true');
		this.querySelector('#uploadPictureField').value = '';
		this.#trackValidationError('unknown');
	}

	#isValidFileType(imageFile) {
		const validImageTypesRegex = new RegExp('gif|jpg|jpeg|png|webp');
		if (validImageTypesRegex.test(imageFile.type)) {
			return true;
		}
		throw { type: 'wrong_format' };
	}

	#isValidFileSize(imageFile) {
		if (imageFile.size <= 10485760) {
			// 10 Megabyte
			return true;
		}
		throw { type: 'file_too_big' };
	}

	#isValidImageDimensions(imageFile) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = (event) => {
				const tempImage = new Image();
				tempImage.onload = () => {
					if (tempImage.width < 500 || tempImage.height < 500) {
						reject({ type: 'file_dimensions' });
					} else {
						resolve(true);
					}
				};
				tempImage.onerror = () => {
					reject({ type: 'file_unavailable' });
				};

				tempImage.src = event.target.result;
			};

			reader.onerror = () => {
				reject({ type: 'file_unavailable' });
			};

			reader.readAsDataURL(imageFile);
		});
	}

	#hideAllUploadErrors() {
		const errorMessages = this.querySelectorAll('* > wdk-hintbox-error');
		errorMessages.forEach((errorElement) => {
			errorElement.removeAttribute('show');
		});

		this.querySelector('#dragDropArea').classList.remove('dragOver');
	}

	#trackValidationError(targetId) {
		jPar.util.log.bigData({
			category: this.getAttribute('tracking-category'),
			subcategory: this.getAttribute('tracking-subcategory'),
			targetId: targetId,
			action: 'error',
		});
	}

	#trackInitUploadButton() {
		jPar.util.log.bigData({
			category: this.getAttribute('tracking-category'),
			subcategory: this.getAttribute('tracking-subcategory'),
			action: 'click',
			targetId: this.getAttribute('tracking-target-id'),
		});
	}

	#trackPhotoSelected() {
		jPar.util.log.bigData({
			category: this.getAttribute('tracking-category'),
			subcategory: this.getAttribute('tracking-subcategory'),
			action: 'click',
			targetId: 'button_select_photo',
		});
	}
}
